import "media-chrome/menu";
import "./component-menu-button";
import "./component-menu-link";
import "./component-share-menu";
import "./component-overlay-button";

import composePlayPauseButton from "./compose-play-pause-button";
import composeMuteButton from "./compose-mute-button";
import resolveEnvironment from "./resolve-environment";
import composeCastButtons from "./compose-cast-buttons";
import resolvePlayerName from "./resolve-player-name";

/**
 * @typedef {import('../../types').Content} Content
 * @typedef {import('../../types').UserOptions} UserOptions
 */

const defaultCustomButtonIcon = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='1.5' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25' /%3E%3C/svg%3E`;

/**
 *
 * @param {UserOptions | any } userOptions - user settings passed into the component. Having to include `any` because there's no easy way of passing only attributes of the class without explicity stating what they are
 * @param {Content} mediaItem - the media item returned from the api
 * @returns {string} - the html string to be rendered
 */
function composeVideoHtml(userOptions, mediaItem) {
  // As well as destructuring to avoid more verbose code, we're also removing these from the userOptions object
  const {
    host,
    token,
    mediaplaying,
    aspectratio,
    islikelive,
    showdescription,
    overridedescription,
    showplaybackratecontrols,
    ismuted,
    shouldautostart,
    skipto,
    canfloat,
    width,
    showcontrols,
    skiprecap,
    skipintro,
    showtitle,
    overridetitle,
    overridethumbnail,
    showbrandlogo,
    showsharingbuttons,
    canbecast,
    isdownloadable,
    showpictureinpicturecontrols,
    overridecolor,
    environment,
    playercode,
    debug,
    beaconcollectiondomain,
    custombuttonimage,
    custombuttontitle,
    custombuttonurl,
    ...remainingUserOptions
  } = userOptions;

  const castButtons = composeCastButtons();

  const userOptionsString = Object.entries(remainingUserOptions)
    .map(([key, value]) => `metadata-${key}="${value}"`)
    .join(", ");

  const playerName = resolvePlayerName(playercode);

  const renderLiveChrome = islikelive === "true" || mediaItem.is_live;

  const shouldShowDescription =
    showdescription !== "false" &&
    (mediaItem.description || overridedescription);

  const { env_key } = resolveEnvironment(environment);

  const mediaElement = `
    <mux-video
      slot="media"
      crossorigin
      ${ismuted === "true" ? "muted" : ""}
      src="${mediaItem.media?.playlist}"
      start-time="${skipto}"
      ${shouldautostart === "true" ? `autoplay` : ""}
      metadata-video-title="${overridetitle ?? mediaItem.title}"
      metadata-video-id="${token}"
      metadata-player-name="${playerName}"
      env-key="${env_key}"
      ${debug === "true" ? "debug" : ""}
      ${beaconcollectiondomain && `beacon-collection-domain=${beaconcollectiondomain}`}
      ${userOptionsString}
    >
      ${
        mediaItem.media?.tracks?.captions
          ? `
        ${mediaItem.media?.tracks?.captions?.map(
          (caption) => `
          <track label="${caption.name}" kind="captions" srclang="en" src="${caption.original_url}" />
        `,
        )}`
          : ""
      }
      ${mediaItem.media?.tracks?.chapters ? `<track default kind="chapters" src="${mediaItem.media?.tracks?.chapters}"></track>` : ""}
      ${mediaItem.media?.tracks?.storyboard ? `<track default kind="metadata" label="thumbnails" src="${mediaItem.media?.tracks?.storyboard}">` : ""}
    </mux-video>
  `;

  const posterImage = `      
    <media-poster-image
      slot="poster"
      src="${overridethumbnail ?? mediaItem.media?.thumbnail}">
    </media-poster-image>
  `;

  const mediaSettingsMenu = `
    <media-settings-menu hidden>
      ${
        !renderLiveChrome && showplaybackratecontrols === "true"
          ? `
        <media-settings-menu-item>
            Speed
            <media-playback-rate-menu slot="submenu" hidden>
              <div slot="title">Speed</div>
            </media-playback-rate-menu>
          </media-settings-menu-item> 
        `
          : ""
      }
      <media-settings-menu-item>
        Quality
        <media-rendition-menu slot="submenu" hidden>
          <div slot="title">Quality</div>
        </media-rendition-menu>
      </media-settings-menu-item>
    </media-settings-menu>
  `;

  const controlBar = `
  ${
    !renderLiveChrome
      ? `
    <div class="timerange">
      <media-time-range></media-time-range>
    </div>
  `
      : ``
  }
  <div class="controls">
    <div class="controls--primary">
    ${composePlayPauseButton()}
      ${
        !renderLiveChrome
          ? `
        <media-seek-backward-button seekoffset="10">
          <div slot="tooltip-content">Rewind 10s</div>
          <svg slot="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" ><path fill="none" d="M0 0h20v20H0z" /><path fill="currentColor" d="M7.666 9.83h-.584a.567.567 0 0 1-.418-.165.567.567 0 0 1-.165-.418.567.567 0 0 1 .165-.418.567.567 0 0 1 .418-.165h1.167a.567.567 0 0 1 .418.165.567.567 0 0 1 .165.418v3.5a.567.567 0 0 1-.165.418.567.567 0 0 1-.418.165.567.567 0 0 1-.418-.165.567.567 0 0 1-.165-.418Zm2.723 3.5a.753.753 0 0 1-.554-.224.753.753 0 0 1-.224-.554V9.44a.753.753 0 0 1 .224-.554.753.753 0 0 1 .554-.224h1.556a.753.753 0 0 1 .554.224.753.753 0 0 1 .224.554v3.112a.753.753 0 0 1-.224.554.753.753 0 0 1-.554.224Zm.389-1.167h.778V9.829h-.778ZM9.997 18a6.774 6.774 0 0 1-2.733-.554 7.117 7.117 0 0 1-2.218-1.5 7.118 7.118 0 0 1-1.5-2.218 6.775 6.775 0 0 1-.554-2.733.753.753 0 0 1 .224-.554.753.753 0 0 1 .554-.224.753.753 0 0 1 .554.224.753.753 0 0 1 .224.554 5.255 5.255 0 0 0 1.585 3.861 5.255 5.255 0 0 0 3.864 1.588 5.255 5.255 0 0 0 3.861-1.585 5.255 5.255 0 0 0 1.585-3.861 5.255 5.255 0 0 0-1.585-3.861A5.255 5.255 0 0 0 9.997 5.55H9.88l.661.661a.713.713 0 0 1 .224.545.821.821 0 0 1-.224.545.793.793 0 0 1-.554.243.724.724 0 0 1-.554-.224l-2-2a.746.746 0 0 1-.233-.545.746.746 0 0 1 .233-.545l2-2a.724.724 0 0 1 .554-.224.793.793 0 0 1 .554.243.821.821 0 0 1 .224.545.713.713 0 0 1-.224.545L9.88 4h.117a6.775 6.775 0 0 1 2.733.554 7.117 7.117 0 0 1 2.218 1.5 7.118 7.118 0 0 1 1.5 2.218 6.775 6.775 0 0 1 .554 2.733 6.775 6.775 0 0 1-.554 2.733 7.118 7.118 0 0 1-1.5 2.218 7.118 7.118 0 0 1-2.218 1.5A6.775 6.775 0 0 1 9.997 18Z"/></svg>
        </media-seek-backward-button>
        <div class="volume-controls">
          ${composeMuteButton()}
          <media-volume-range></media-volume-range>
        </div>
        <div class="time-duration-display">
          <media-time-display></media-time-display>
          <span>/</span>
          <media-duration-display></media-duration-display>
        </div>
        `
          : `
          <div class="volume-controls">
            ${composeMuteButton()}
            <media-volume-range></media-volume-range>
          </div>
        `
      }
        ${islikelive === "true" || mediaItem.is_live ? `<media-live-button></media-live-button>` : ""}
      </div>
      <div class="controls--secondary">
        <slot name="premiere-button"></slot>
        ${
          showsharingbuttons !== "false"
            ? `
          <share-menu></share-menu>
        `
            : ""
        }
        ${
          (mediaItem.player?.button?.url || custombuttonurl) && showbrandlogo !== "false"
            ? `
          <menu-link
            showTooltip="true"
            customtitle="${custombuttontitle ?? mediaItem.player?.button?.title ?? ""}"
            customimage="${defaultCustomButtonIcon}"
            customurl="${custombuttonurl ?? mediaItem.player?.button?.url}"
          ></menu-link>
        `
            : ""
        }
        <media-settings-menu-button>
          <svg slot="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.2745 22C9.96896 22 9.70087 21.9 9.47021 21.7C9.23971 21.5 9.10221 21.25 9.05771 20.95L8.73271 18.8333C8.44937 18.7278 8.15912 18.5889 7.86196 18.4167C7.56479 18.2444 7.29671 18.0638 7.05771 17.875L5.10771 18.7583C4.81338 18.8861 4.51896 18.9 4.22446 18.8C3.92996 18.7 3.69938 18.5139 3.53271 18.2418L2.17446 15.825C2.00779 15.5528 1.96054 15.2668 2.03271 14.9668C2.10504 14.6668 2.26621 14.4195 2.51621 14.225L4.29121 12.9082C4.26338 12.7694 4.24671 12.6181 4.24121 12.4543C4.23554 12.2903 4.23271 12.1388 4.23271 12C4.23271 11.8612 4.23554 11.7098 4.24121 11.5457C4.24671 11.3819 4.26338 11.2306 4.29121 11.0918L2.51621 9.775C2.26621 9.5805 2.10504 9.33325 2.03271 9.03325C1.96054 8.73325 2.00779 8.44717 2.17446 8.175L3.53271 5.75825C3.69938 5.48608 3.92996 5.3 4.22446 5.2C4.51896 5.1 4.81338 5.11392 5.10771 5.24175L7.05771 6.125C7.29671 5.93617 7.56621 5.75558 7.86621 5.58325C8.16621 5.41108 8.45504 5.27775 8.73271 5.18325L9.05771 3.05C9.10221 2.75 9.23971 2.5 9.47021 2.3C9.70087 2.1 9.96896 2 10.2745 2H13.0245C13.33 2 13.598 2.1 13.8287 2.3C14.0592 2.5 14.1967 2.75 14.2412 3.05L14.5662 5.16675C14.8495 5.27225 15.1425 5.40833 15.4452 5.575C15.748 5.74167 16.0134 5.925 16.2412 6.125L18.1912 5.24175C18.4855 5.11392 18.7772 5.1 19.0662 5.2C19.355 5.3 19.5828 5.48608 19.7495 5.75825L21.1245 8.175C21.2911 8.44717 21.3384 8.73608 21.2662 9.04175C21.1939 9.34725 21.0327 9.59167 20.7827 9.775L19.0077 11.0583C19.0355 11.2083 19.0522 11.3653 19.0577 11.5293C19.0634 11.6931 19.0662 11.85 19.0662 12C19.0662 12.15 19.0634 12.3042 19.0577 12.4625C19.0522 12.6208 19.0328 12.775 18.9995 12.925L20.7745 14.225C21.0245 14.4083 21.1855 14.6527 21.2577 14.9582C21.33 15.2639 21.2829 15.5528 21.1162 15.825L19.7495 18.2418C19.5828 18.5139 19.3522 18.7 19.0577 18.8C18.7634 18.9 18.469 18.8861 18.1745 18.7583L16.2412 17.875C16.0022 18.0638 15.7355 18.2472 15.4412 18.425C15.1467 18.6028 14.855 18.7389 14.5662 18.8333L14.2412 20.95C14.1967 21.25 14.0592 21.5 13.8287 21.7C13.598 21.9 13.33 22 13.0245 22H10.2745ZM11.6662 15.3332C12.5884 15.3332 13.3745 15.0083 14.0245 14.3582C14.6745 13.7083 14.9995 12.9222 14.9995 12C14.9995 11.0778 14.6745 10.2917 14.0245 9.64175C13.3745 8.99175 12.5884 8.66675 11.6662 8.66675C10.7384 8.66675 9.95087 8.99175 9.30371 9.64175C8.65638 10.2917 8.33271 11.0778 8.33271 12C8.33271 12.9222 8.65638 13.7083 9.30371 14.3582C9.95087 15.0083 10.7384 15.3332 11.6662 15.3332Z" fill="currentColor"/>
          </svg>
        </media-settings-menu-button>
        ${
          isdownloadable !== "false" &&
          !renderLiveChrome &&
          mediaItem.media?.downloads?.length
            ? `
          <menu-button customtitle="Download" id="downloads-menu">
            <svg slot="icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.9972 16.7188C11.8601 16.7188 11.7326 16.6961 11.6147 16.6509C11.4966 16.6059 11.382 16.5278 11.2709 16.4166L6.625 11.7709C6.41667 11.5589 6.31771 11.3114 6.32812 11.0284C6.33854 10.7455 6.44177 10.4999 6.63781 10.2916C6.85156 10.0832 7.1025 9.9774 7.39062 9.97406C7.67875 9.97052 7.92698 10.0729 8.13531 10.2812L10.9584 13.125V3.04156C10.9584 2.74656 11.0585 2.49917 11.2587 2.29938C11.4592 2.09979 11.7075 2 12.0037 2C12.2998 2 12.5468 2.09979 12.7447 2.29938C12.9426 2.49917 13.0416 2.74656 13.0416 3.04156V13.125L15.8853 10.2812C16.0909 10.0729 16.3351 9.97396 16.6178 9.98438C16.9003 9.99479 17.1484 10.1042 17.3622 10.3125C17.5582 10.5208 17.658 10.7674 17.6616 11.0522C17.6649 11.3368 17.5624 11.5832 17.3541 11.7916L12.7291 16.4166C12.618 16.5278 12.5025 16.6059 12.3825 16.6509C12.2627 16.6961 12.1343 16.7188 11.9972 16.7188ZM4.08344 22C3.52094 22 3.03302 21.7934 2.61969 21.3803C2.20656 20.967 2 20.4791 2 19.9166V16.7291C2 16.4341 2.10021 16.1867 2.30062 15.9869C2.50083 15.7873 2.74906 15.6875 3.04531 15.6875C3.34156 15.6875 3.58865 15.7873 3.78656 15.9869C3.98448 16.1867 4.08344 16.4341 4.08344 16.7291V19.9166H19.9166V16.7291C19.9166 16.4341 20.0168 16.1867 20.2172 15.9869C20.4176 15.7873 20.6658 15.6875 20.9619 15.6875C21.2581 15.6875 21.5052 15.7873 21.7031 15.9869C21.901 16.1867 22 16.4341 22 16.7291V19.9166C22 20.4791 21.7934 20.967 21.3803 21.3803C20.967 21.7934 20.4791 22 19.9166 22H4.08344Z" fill="currentColor"/>
          </svg>
          </menu-button>
        `
            : ""
        }
        ${canbecast !== "false" ? castButtons : ""}
        ${
          mediaItem.media?.tracks?.captions?.length
            ? `
          <media-captions-menu-button>
          <svg slot="on" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.85194 20.7778C3.35194 20.7778 2.91824 20.5942 2.55083 20.2269C2.18361 19.8595 2 19.4258 2 18.9258V4.85194C2 4.35194 2.18361 3.91824 2.55083 3.55083C2.91824 3.18361 3.35194 3 3.85194 3H20.1481C20.6481 3 21.0818 3.18361 21.4492 3.55083C21.8164 3.91824 22 4.35194 22 4.85194V18.9258C22 19.4258 21.8164 19.8595 21.4492 20.2269C21.0818 20.5942 20.6481 20.7778 20.1481 20.7778H3.85194ZM6.25917 15.2036H10C10.2624 15.2036 10.4823 15.1149 10.6597 14.9375C10.8371 14.7601 10.9258 14.5402 10.9258 14.2778V13.7222C10.9258 13.545 10.8668 13.3973 10.7486 13.2792C10.6305 13.161 10.4828 13.1019 10.3056 13.1019H10.0647C9.8875 13.1019 9.73982 13.161 9.62167 13.2792C9.50352 13.3973 9.44444 13.545 9.44444 13.7222H6.81472V10.0556H9.44444C9.44444 10.2346 9.50352 10.3828 9.62167 10.5C9.73982 10.6172 9.8875 10.6758 10.0647 10.6758H10.3056C10.4828 10.6758 10.6305 10.6168 10.7486 10.4986C10.8668 10.3805 10.9258 10.2328 10.9258 10.0556V9.5C10.9258 9.23759 10.8371 9.01769 10.6597 8.84028C10.4823 8.66287 10.2624 8.57417 10 8.57417H6.25917C5.99694 8.57417 5.77704 8.66287 5.59944 8.84028C5.42204 9.01769 5.33333 9.23759 5.33333 9.5V14.2778C5.33333 14.5402 5.42204 14.7601 5.59944 14.9375C5.77704 15.1149 5.99694 15.2036 6.25917 15.2036ZM17.7592 8.57417H14.0186C13.7562 8.57417 13.5363 8.66287 13.3589 8.84028C13.1813 9.01769 13.0925 9.23759 13.0925 9.5V14.2778C13.0925 14.5402 13.1813 14.7601 13.3589 14.9375C13.5363 15.1149 13.7562 15.2036 14.0186 15.2036H17.7592C18.0216 15.2036 18.2415 15.1149 18.4189 14.9375C18.5965 14.7601 18.6853 14.5402 18.6853 14.2778V13.7222C18.6853 13.545 18.6262 13.3973 18.5081 13.2792C18.3897 13.161 18.2419 13.1019 18.0647 13.1019H17.8242C17.6469 13.1019 17.4992 13.161 17.3808 13.2792C17.2627 13.3973 17.2036 13.545 17.2036 13.7222H14.5742V10.0556H17.2036C17.2036 10.2346 17.2627 10.3828 17.3808 10.5C17.4992 10.6172 17.6469 10.6758 17.8242 10.6758H18.0647C18.2419 10.6758 18.3897 10.6168 18.5081 10.4986C18.6262 10.3805 18.6853 10.2328 18.6853 10.0556V9.5C18.6853 9.23759 18.5965 9.01769 18.4189 8.84028C18.2415 8.66287 18.0216 8.57417 17.7592 8.57417Z" fill="currentColor"/>
            </svg>
            <svg slot="off" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.6572 22L17.5477 18.8905H6.07538C5.55713 18.8905 5.11347 18.7059 4.74441 18.3369C4.37534 17.9678 4.19081 17.5241 4.19081 17.0059V5.53357L2 3.34276L3.34276 2L22 20.6572L20.6572 22ZM21.1519 17.1237L17.9953 13.967C18.1052 13.8885 18.1877 13.7825 18.2426 13.649C18.2976 13.5155 18.3251 13.3781 18.3251 13.2367V12.2945H16.9117V12.7656H16.7939L15.0271 10.9988V9.93875H16.9117V10.4099H18.3251V9.46761C18.3251 9.20063 18.2348 8.97684 18.0542 8.79623C17.8736 8.61563 17.6498 8.52532 17.3828 8.52532H14.5559C14.289 8.52532 14.0652 8.61563 13.8846 8.79623C13.704 8.97684 13.6137 9.20063 13.6137 9.46761V9.58539L7.84217 3.8139H19.2674C19.7856 3.8139 20.2293 3.99843 20.5984 4.36749C20.9674 4.73655 21.1519 5.18021 21.1519 5.69847V17.1237ZM7.95995 14.179H10.7868C11.0538 14.179 11.2776 14.0887 11.4582 13.9081C11.6388 13.7275 11.7291 13.5037 11.7291 13.2367V13.0718L10.9517 12.2945H10.3157V12.7656H8.4311V9.77385L7.37102 8.71378C7.26109 8.7923 7.17472 8.89831 7.1119 9.0318C7.04908 9.16529 7.01767 9.31056 7.01767 9.46761V13.2367C7.01767 13.5037 7.10797 13.7275 7.28857 13.9081C7.46918 14.0887 7.69297 14.179 7.95995 14.179Z" fill="currentColor"/>
              </svg>
          </media-captions-menu-button>
        `
            : ""
        }
        ${
          showpictureinpicturecontrols === "true" && canfloat !== "true"
            ? `
          <media-pip-button>
            <div slot="tooltip-content">Picture in Picture</div>
            <svg slot="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fill="none" d="M0 0h20v20H0z" /><path fill="currentColor" d="M7.6 13.2a.774.774 0 0 1-.57-.23.774.774 0 0 1-.23-.57V7.6a.774.774 0 0 1 .23-.57.774.774 0 0 1 .57-.23H14a.774.774 0 0 1 .57.23.774.774 0 0 1 .23.57v4.8a.774.774 0 0 1-.23.57.774.774 0 0 1-.57.23Zm-4.8 3.2a.774.774 0 0 1-.57-.23.774.774 0 0 1-.23-.57.774.774 0 0 1 .23-.57.774.774 0 0 1 .57-.23h13.6V4.4a.774.774 0 0 1 .23-.57.774.774 0 0 1 .57-.23.774.774 0 0 1 .57.23.774.774 0 0 1 .23.57v10.4a1.541 1.541 0 0 1-.47 1.13 1.541 1.541 0 0 1-1.13.47Z"/></svg>
          </media-pip-button>
        `
            : ""
        }
        <media-fullscreen-button>
          <div slot="tooltip-content">Fullscreen</div>
          <svg slot="enter" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" ><path fill="none" d="M0 0h20v20H0z" /><path fill="currentColor" d="M3.778 16.222h1.778a.86.86 0 0 1 .633.256.86.86 0 0 1 .256.633.86.86 0 0 1-.256.633.86.86 0 0 1-.633.256H2.889a.86.86 0 0 1-.633-.256.86.86 0 0 1-.256-.633v-2.667a.86.86 0 0 1 .256-.633.86.86 0 0 1 .633-.256.86.86 0 0 1 .633.256.86.86 0 0 1 .256.633Zm12.444 0v-1.778a.86.86 0 0 1 .256-.633.86.86 0 0 1 .633-.256.86.86 0 0 1 .633.256.86.86 0 0 1 .256.633v2.667a.86.86 0 0 1-.256.633.86.86 0 0 1-.633.256h-2.667a.86.86 0 0 1-.633-.256.86.86 0 0 1-.256-.633.86.86 0 0 1 .256-.633.86.86 0 0 1 .633-.256ZM3.778 3.778v1.778a.86.86 0 0 1-.256.633.86.86 0 0 1-.633.256.86.86 0 0 1-.633-.256A.86.86 0 0 1 2 5.556V2.889a.86.86 0 0 1 .256-.633.86.86 0 0 1 .633-.255h2.667a.86.86 0 0 1 .633.255.86.86 0 0 1 .256.633.86.86 0 0 1-.256.633.86.86 0 0 1-.633.256Zm12.444 0h-1.778a.86.86 0 0 1-.633-.256.86.86 0 0 1-.256-.633.86.86 0 0 1 .256-.633.86.86 0 0 1 .633-.255h2.667a.86.86 0 0 1 .633.255.86.86 0 0 1 .256.633v2.667a.86.86 0 0 1-.256.633.86.86 0 0 1-.633.256.86.86 0 0 1-.633-.256.86.86 0 0 1-.256-.633Z"/></svg>
          <svg slot="exit" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><g><path fill="currentColor" d="M4.667 18v-2.667H2v-1.778h4.444V18Zm8.889 0v-4.444H18v1.778h-2.667V18ZM2 6.444V4.666h2.667V2h1.778v4.444Zm11.556 0V2h1.778v2.667H18v1.778Z"/><path fill="none" d="M0 0h20v20H0z" /></g></svg>
        </media-fullscreen-button>
      </div>
    </div>
  `;

  const videoMeta = `
    <div class="video-meta">
      ${showtitle !== "false" ? `<h1 slot="title">${overridetitle ?? mediaItem.title}</h1>` : ""}
      ${shouldShowDescription ? `<p slot="description">${overridedescription ?? mediaItem.description}</p>` : ""}
    </div>
  `;

  const skipButtons = `
    <div class="skip-buttons">
      ${!renderLiveChrome && showcontrols === "true" && skiprecap === "true" ? `<overlay-button title="skip-recap" text="Skip recap" hidden="true"></overlay-button>` : ""}
      ${!renderLiveChrome && showcontrols === "true" && skipintro === "true" ? `<overlay-button title="skip-intro" text="Skip intro"  hidden="true"></overlay-button>` : ""}
    </div>
  `;

  return `
      <media-controller 
        ${renderLiveChrome ? `defaultstreamtype="live"` : ""}
        ${mediaplaying !== "false" && showcontrols ? "showcontrols" : ""}
        ${showcontrols === "false" ? "gesturesdisabled" : ""}
      >
        ${composeMuteButton("unmute")}
        ${
          mediaItem.is_live && mediaItem.status && mediaItem.status !== "Active"
            ? `
          <div class="offline-notice">
            <div>
              <h2>Stream is not active</h2>
              <p>Checking for updates in less than 30 seconds.</p>
            </div>
          </div>
        `
            : `
          ${mediaElement}
        `
        }
        <media-loading-indicator slot="centered-chrome" noautohide=""></media-loading-indicator>
        ${
          mediaItem.status && mediaItem.status !== "Active"
            ? ""
            : `
            ${showcontrols !== "false" ? `${composePlayPauseButton(null, true)}` : ""}
        `
        }
        <media-captions-menu hidden anchor="auto"></media-captions-menu>
        ${(overridethumbnail ?? mediaItem.media?.thumbnail) ? `${posterImage}` : ""}
        ${mediaSettingsMenu}
        ${
          !renderLiveChrome && showplaybackratecontrols !== "false"
            ? `
          <media-playback-rate-menu hidden anchor="auto"></media-playback-rate-menu>
        `
            : ""
        }
        ${videoMeta}
        ${skipButtons}
        ${showcontrols === "true" ? `${controlBar}` : ""}
        <div class="floating-player-panel">
          <button title="Close floating player">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke-width="1.5" stroke="white">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </media-controller>
  `;
}
export default composeVideoHtml;
